// src/pages/ElCristo.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import useSettingsMountEffect from '../hooks/useSettingsMountEffect';
import { 
  KidsaPreloader, 
  KidsaHeader, 
  KidsaOffcanvas,
  KidsaHero,
  KidsaAboutUs,
  KidsaFeatures,
  KidsaFooter,
  KidsaPrograms,
  KidsaActivities,
  KidsaTeam,
  KidsaTestimonial,
  KidsaCta,
  KidsaNews,
  KidsaNewsLetter,
  KidsaInstagram,
  KidsaCounter,
  KidsaBrands,
  KidsaVideo,
  KidsaWorkProcess,
  KidsaNosotros,
  KidsaRadio
} from '../components/kidsa';
import { kidsaScripts, loadScriptsSequentially, cleanupScripts } from '../service/scriptLoader';
import apiElCristo, { type SiteContent } from '../service/api_elcristo';

// Importar estilos
import '../assets/kidsa/assets/css/main.css';
import '../assets/kidsa/assets/css/nice-select.css';
import '../assets/kidsa/assets/css/swiper-bundle.min.css';
import '../assets/kidsa/assets/css/meanmenu.css';
import '../assets/kidsa/assets/css/magnific-popup.css';
import '../assets/kidsa/assets/css/animate.css';
import '../assets/kidsa/assets/css/all.min.css';
import '../assets/kidsa/assets/css/bootstrap.min.css';
import '../assets/kidsa/assets/css/instagram.css';

const ElCristo: React.FC = () => {
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [contentData, setContentData] = useState<SiteContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const pathName = location.pathname;
  
  // Verifica la ruta exacta (dev mode) (prod mode /) /elcristo o en alguna subruta ql /elcristo/*
  const isHomePage = pathName === '/elcristo' || pathName === '/elcristo/';
  const isSubRoute = pathName.startsWith('/elcristo/') && pathName !== '/elcristo/';

  useSettingsMountEffect({
    disableNavigationType: true,
    disableHorizontalNavbarAppearance: true,
    disableVerticalNavbarAppearance: true,
    disableHorizontalNavbarShape: true
  });

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    if (url.startsWith('#') && !isHomePage) {
      e.preventDefault();
      navigate('/elcristo' + url);
    }
    else if (url.startsWith('./') && url.includes('#') && !isHomePage) {
      e.preventDefault();
      const hash = url.substring(url.indexOf('#'));
      navigate('/elcristo' + hash);
    }
  };

  useEffect(() => {
    if (isHomePage && location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [isHomePage, location.hash]);

  // Fetch content from API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await apiElCristo.getSiteContent();
        setContentData(data);
      } catch (err) {
        console.error('Error fetching content:', err);
        setError(err instanceof Error ? err.message : 'Failed to load content');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, []);

  useEffect(() => {
    loadScriptsSequentially(kidsaScripts);
    
    const handleSidebarToggle = () => {
      setOffcanvasOpen(true);
    };

    document.addEventListener('click', (e) => {
      const target = e.target as Element;
      if (target.closest('.sidebar__toggle')) {
        handleSidebarToggle();
      }
    });

    return () => {
      cleanupScripts(kidsaScripts);
    };
  }, []);

  const createMenuItem = (item: any) => {
    const menuItem = { ...item };
    
    if (menuItem.subMenu) {
      menuItem.subMenu = menuItem.subMenu.map((subItem: any) => {
        return {
          ...subItem,
          onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleNavigation(e, subItem.url)
        };
      });
    } else {
      menuItem.onClick = (e: React.MouseEvent<HTMLAnchorElement>) => handleNavigation(e, menuItem.url);
    }
    
    return menuItem;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white">
        <KidsaPreloader letters="EL CRISTO" />
      </div>
    );
  }

  // Error state - Página de mantenimiento
  if (error || !contentData) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #70A6B1 0%, #385469 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          opacity: 0.1
        }}>
          <img 
            src="/assets/kidsa/assets/img/breadcrumb-shape/plane.png" 
            alt="decoration"
            style={{ width: '100px', height: 'auto' }}
          />
        </div>
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '8%',
          opacity: 0.1
        }}>
          <img 
            src="/assets/kidsa/assets/img/breadcrumb-shape/bee.png" 
            alt="decoration"
            style={{ width: '80px', height: 'auto' }}
          />
        </div>
        <div style={{ 
          textAlign: 'center', 
          maxWidth: '600px',
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '60px 40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ marginBottom: '30px' }}>
            <img 
              src="/assets/kidsa/assets/img/logo/logoheader.png" 
              alt="Colegio El Cristo"
              style={{ maxWidth: '200px', height: 'auto' }}
            />
          </div>
          <div style={{ 
            marginBottom: '30px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              backgroundColor: '#d5f0ff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-tools" style={{ 
                fontSize: '40px', 
                color: '#ff0f00' 
              }}></i>
            </div>
          </div>
          <h1 style={{ 
            color: '#011b47',
            fontSize: '32px',
            fontWeight: 'bold',
            marginBottom: '20px',
            fontFamily: 'Jost, sans-serif'
          }}>
            Sitio en Mantenimiento
          </h1>
          <p style={{ 
            color: '#6c757d',
            fontSize: '18px',
            lineHeight: '1.6',
            marginBottom: '30px'
          }}>
            Estamos realizando mejoras para brindarte una mejor experiencia. 
            Volveremos pronto.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            style={{
              padding: '15px 40px',
              backgroundColor: '#ff0f00',
              color: 'white',
              border: 'none',
              borderRadius: '50px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              boxShadow: '0 8px 20px rgba(255, 15, 0, 0.3)',
              transition: 'all 0.3s ease',
              fontFamily: 'Jost, sans-serif'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#385469';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 30px rgba(56, 84, 105, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ff0f00';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(255, 15, 0, 0.3)';
            }}
          >
            Reintentar Conexión
          </button>
          <div style={{ 
            marginTop: '40px',
            paddingTop: '30px',
            borderTop: '1px solid #e0e0e0'
          }}>
            <p style={{ 
              color: '#385469',
              fontSize: '14px',
              marginBottom: '10px'
            }}>
              ¿Necesitas ayuda?
            </p>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '20px',
              flexWrap: 'wrap'
            }}>
              <a 
                href="tel:+56946966778"
                style={{ 
                  color: '#70A6B1',
                  textDecoration: 'none',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <i className="fas fa-phone"></i>
                +56 9 4696 6778
              </a>
              <a 
                href="mailto:colegio@colegioelcristo.cl"
                style={{ 
                  color: '#70A6B1',
                  textDecoration: 'none',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <i className="fas fa-envelope"></i>
                colegio@colegioelcristo.cl
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Process menuItems with navigation handlers
  const headerData = {
    ...contentData.header,
    menuItems: contentData.header.menuItems.map(createMenuItem)
  };

  // Offcanvas con fallback completo
  const offcanvasData = {
    logo: contentData.offcanvas?.logo || "/assets/kidsa/assets/img/logo/logo.png",
    description: contentData.offcanvas?.description || "Colegio El Cristo",
    contactInfo: contentData.offcanvas?.contactInfo || {
      address: "Camino Interior Nº211, El Monte",
      email: "colegio@colegioelcristo.cl",
      schedule: "Lun-Vie, 7:30am - 4:00pm",
      phone: "+56 9 4696 6778"
    },
    socialLinks: contentData.offcanvas?.socialLinks || {
      facebook: "#",
      twitter: "#",
      youtube: "#",
      linkedin: "#"
    },
    ctaButton: {
      ...(contentData.offcanvas?.ctaButton || { text: "Contacto", url: "./#contacto" }),
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => 
        handleNavigation(e, contentData.offcanvas?.ctaButton?.url || "./#contacto")
    },
    isOpen: offcanvasOpen,
    onClose: () => setOffcanvasOpen(false)
  };

  // Hero con fallback completo
  const heroData = contentData.hero || {
    title: `Año escolar <span>${new Date().getFullYear()}</span>`,
    subtitle: "Bienvenidos",
    description: "Este año es una oportunidad para aprender y crecer juntos.",
    primaryButtonText: "Conócenos",
    primaryButtonLink: "#nosotros",
    secondaryButtonText: "Más información",
    secondaryButtonLink: "#",
    heroImage: "/assets/kidsa/assets/img/hero/hero-shape.png",
    heroBackgroundImage: "/assets/kidsa/assets/img/hero/bottom.png"
  };

  const featuresData = contentData.features || { features: [] };
  
  // AboutUs con fallback completo
  const aboutusData = contentData.aboutUs || {
    subtitle: "Nosotros",
    title: "Educación de Calidad",
    description: "Nuestro colegio se basa en el derecho que todos los estudiantes reciban educación de calidad.",
    bulletPoints: [],
    exploreMoreLink: "#nosotros",
    contactNumber: "+56 9 4696 6778",
    images: {
      bus: "/assets/kidsa/assets/img/about/bus.png",
      girl: "/assets/kidsa/assets/img/about/backpack.png",
      dot: "/assets/kidsa/assets/img/about/dot.png",
      main: "/assets/kidsa/assets/img/about/02.jpg",
      secondary: "/assets/kidsa/assets/img/about/02.png",
      borderShape: "/assets/kidsa/assets/img/about/border-shape-1.png"
    }
  };
  
  // Video con fallback completo
  const videoData = contentData.video || {
    backgroundImage: "/assets/kidsa/assets/img/video-bg.png",
    videoUrl: "https://www.youtube.com/watch?v=HPYWlBPQ1WM"
  };
  
  const programsData = contentData.programs || { 
    title: "Nuestros niveles educativos",
    subtitle: "Programas",
    programs: [] 
  };
  
  const workProcessData = contentData.workProcess || { workProcessItems: [] };
  const activitiesData = contentData.activities || { 
    subtitle: "Actividades",
    title: "Nuestras actividades",
    wordsbutton: "Más información",
    wordslink: "#",
    description: "Descubre todas nuestras actividades.",
    image: "/assets/kidsa/assets/img/about/03.jpg",
    radiusShape: "/assets/kidsa/assets/img/about/radius-shape-1.png",
    activities: [] 
  };
  
  // Team con fallback - IMPORTANTE: subtitle y title son arrays
  const teamData = contentData.team ? {
    ...contentData.team,
    subtitle: Array.isArray(contentData.team.subtitle) 
      ? contentData.team.subtitle 
      : [contentData.team.subtitle || "Nuestro Equipo"],
    title: Array.isArray(contentData.team.title)
      ? contentData.team.title
      : [contentData.team.title || "Equipo Docente"]
  } : {
    subtitle: ["Nuestro Equipo"],
    title: ["Equipo Docente"],
    teamMembers: []
  };
  
  // Testimonial con estructura correcta
  const testimonialData = contentData.testimonial || {
    subtitle: "Comunicados",
    title: "Anuncios importantes",
    testimonials: []
  };
  
  const ctaData = contentData.cta || {
    subtitle: "Admisión",
    title: `Únete a nuestra comunidad escolar ${new Date().getFullYear() + 1}`,
    backgroundImage: "/assets/kidsa/assets/img/cta/cta-bg.jpg",
    ctaImage: "/assets/kidsa/assets/img/cta/cta-2.png",
    ctaShape: "/assets/kidsa/assets/img/cta/cta-shape.png",
    ctaButtonText: "Más información",
    ctaButtonLink: "#admision",
    planeShape: "/assets/kidsa/assets/img/cta/plane.png",
    pencilShape: "/assets/kidsa/assets/img/red.png"
  };
  
  const newsData = contentData.news || {
    subtitle: "Noticias",
    title: "Últimas noticias",
    featuredNews: {
      image: "/assets/kidsa/assets/img/news/01.jpg",
      category: "Noticias",
      date: new Date().toLocaleDateString(),
      title: "Bienvenidos",
      description: "Contenido próximamente",
      author: {
        name: "Colegio El Cristo",
        image: "/assets/kidsa/assets/img/news/author.png",
        role: "Administración"
      },
      link: "#"
    },
    additionalNews: []
  };
  
  const instagramData = contentData.instagram || { 
    title: "Síguenos en Instagram",
    instagram: "colegioelcristo",
    posts: []
  };

  // Newsletter desde API con fallback completo
  const newsletterData = contentData.newsletter || {
    subtitle: "Boletines Informativos",
    title: "¡Suscríbete a nuestro boletín!",
    mailRoot: "hvillarroel@linuxcorp.cl",
    emailPlaceholder: "tu@email",
    subscribeButtonText: "Suscríbete!",
    planeShape: "/assets/kidsa/assets/img/plane.png",
    pencilShape: "/assets/kidsa/assets/img/cta/pencil-2.png",
    ctaShape: "/assets/kidsa/assets/img/cta/shape.png"
  };

  // Brands desde API con fallback completo
  const brandsData = contentData.brands || {
    title: "Quienes nos respaldan",
    brands: []
  };

  const footerData = {
    logo: "/assets/kidsa/assets/img/logo/logo.png",
    description: "Una comunidad escolar comprometida con la excelencia educativa y el desarrollo integral de nuestros estudiantes.",
    quickLinks: [
      { text: "Inicio", url: "./#" },
      { text: "Nosotros", url: "./#nosotros" },
      { text: "Modelo Educativo", url: "./#modelo" },
      { text: "Admisión", url: "./#admision" },
      { text: "Noticias", url: "./#noticias" },
      { text: "Contacto", url: "./#contacto" }
    ].map(link => ({
      ...link,
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleNavigation(e, link.url)
    })),
    categories: [
      { text: "Eventos", url: "./#noticias" },
      { text: "Anuncios", url: "./#comunicados" },
      { text: "Cultura", url: "./#modelo" }
    ].map(link => ({
      ...link,
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleNavigation(e, link.url)
    })),
    contactInfo: [
      {
        icon: "fa-solid fa-phone",
        title: "Llámanos",
        description: "+56 9 4696 6778",
        link: "tel:+56946966778"
      },
      {
        icon: "fa-solid fa-envelope",
        title: "Escríbenos",
        description: "colegio@colegioelcristo.cl",
        link: "mailto:colegio@colegioelcristo.cl"
      },
      {
        icon: "fa-solid fa-map-marker-alt",
        title: "Encuentranos",
        description: "Camino Interior Nº211.",
        link: "#"
      }
    ],
    recentPosts: [
      {
        image: "/assets/kidsa/assets/img/news/pp1.jpg",
        date: "24 Abr, 2025",
        title: "Nuestra nueva página Web",
        link: "./#noticias",
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleNavigation(e, "./#noticias")
      },
      {
        image: "/assets/kidsa/assets/img/news/pp2.jpg",
        date: "19 Abr, 2025",
        title: "Celebración del día de la Tierra...",
        link: "./#noticias",
        onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleNavigation(e, "./#noticias")
      }
    ],
    copyrightText: `© Copyright ${new Date().getFullYear()} <a href='https://www.creatimus.com'>Creatimus Linuxcorp </a>`,
    termsLink: "#",
    privacyLink: "#",
    socialLinks: contentData.header.socialLinks
  };

  const nosotrosData = {
    title: "Colegio El Cristo",
    subtitle: "Historia",
    main: {
      title: "Nuestro Colegio",
      toptitle: "Historia",
      description: `La escuela rural G-744 El Cristo, se encuentra ubicada en el sector de Chiñigue, en las cercanías del límite de las comuna de El Monte y Melipilla, específicamente en el kilómetro 57 de la ruta 78, camino interior Chiñigue El Cristo Nº 211.

Fue fundada el 15 de noviembre de 1937, como escuela particular del fundo de la zona cuyos dueños eran la familia Tagle Irarrázabal. La escuela funcionaba en la antigua casa patronal, atendiendo a los hijos de los trabajadores del predio; siendo una de las primeras escuelas rurales de las provincias de Talagante y Melipilla.

En 1958 se construyó una sala de clases que cumplía con las exigencias del Ministerio de Educación y es, en ese año, cuando adquiere la calidad de Cooperadora de la Función Educativa del Estado. La escuela comenzó como unidocente, pues era solo un profesor quien enseñaba a los diferentes niveles.

En el año 1968 pasó a tuición fiscal son donación de terreno. Posteriormente, en 1982 la Ilustre Municipalidad de El Monte recibe la tuición administrativa de la escuela y ordena construir dos nuevas aulas de madera. En 1994 con recursos del Fondo Nacional de Desarrollo Regional (FNDR) es repuesto el antiguo local, construyéndose un pabellón para párvulos, que incluye: un aula, un aula de hábitos higiénicos y bodega, un pabellón administrativo, un aula para nivel básico y cocina, también se construye el cierre exterior de la escuela y 75m2 de patio techado. En el año 1995 se crea el séptimo año básico y se proyecta para el año 1996 el octavo año básico. En el 2003 son inauguradas las nuevas dependencias de material concreto con dos pisos y siete salas de clases, un comedor, una biblioteca, sala de párvulos, camarines, aula de recursos para grupo diferencial, oficina de UTP, cancha asfaltada y patio techado; todo esto gracias a aportes foráneos y la gestión de la Municipalidad que se adjudicó un Proyecto de Implementación de nuevas dependencias, lo que permitió el funcionamiento de la jornada escolar completa a partir del 1 de septiembre del 2003.

Durante todos estos años su finalidad ha sido ir contribuyendo al desarrollo de sus habilidades cognitivas, socioemocionales y éticas. A lo largo de su trayectoria, se ha consolidado como un espacio acogedor, inclusivo y comprometido con la formación valórica y académica.`
    },
    info: {
      title: "Datos Estadísticos",
      fundacion: "Fundación", 
      fundacion_res: "15 Nov 1937",
      horario: "Horario",
      horario_res: "08:00 a 16:00",
      profesores: "Profesores",
      profesores_res: "XX",
      estudiantes: "Estudiantes",
      estudiantes_res: "XX",
      certificaciones: "Certificaciones",
      certificaciones_res: "XX",
      asistentes: "Asistentes Ed.",
      asistentes_res: "XX"
    },
    mision: {
      title: "Nuestra Misión",
      toptitle: "Misión",
      description: "En el Colegio El Cristo, nuestra misión es formar individuos íntegros en un ambiente seguro y respetuoso. Nos dedicamos a promover aprendizajes significativos y pertinentes, asegurando una gestión pedagógica de alta calidad. Fomentamos el bienestar socioemocional y el desarrollo de habilidades para la vida, impulsando la participación activa de toda la comunidad educativa. Cada miembro es esencial para cultivar un entorno de aprendizaje enriquecedor y colaborativo.",
      title_right: "Misión Colegio El Cristo",
      item1: "Formación y respeto.",
      item2: "Desarrollo de habilidades.",
      item3: "Comunidad Escolar.",
      item4: "Educación y Cultura",
      item5: "Apoyo y contención",
      item6: "Amistad y valores",
    },
    vision: {
      title: "Nuestra Visión",
      toptitle: "Visión",
      description: `Aspiramos a ser una comunidad educativa de referencia, distinguida por su calidad humana, compromiso social y excelencia académica. Nuestro propósito es formar estudiantes reflexivos, solidarios y con un profundo sentido de responsabilidad ciudadana, equipándolos para enfrentar los desafíos del siglo XXI con integridad, autonomía y un espíritu crítico. 
                            En el Colegio El Cristo, cultivamos líderes del mañana que contribuyen activamente al bienestar de su entorno y a la construcción de un futuro más justo y equitativo.`
    },
    images: {
        head: "/assets/kidsa/assets/img/section-bottom-shape.png",
        line: "/assets/kidsa/assets/img/breadcrumb-shape/line.png",
        plane: "/assets/kidsa/assets/img/breadcrumb-shape/plane.png",
        doll: "/assets/kidsa/assets/img/breadcrumb-shape/doll.png",
        parasuit: "/assets/kidsa/assets/img/breadcrumb-shape/parasuit.png",
        frame: "/assets/kidsa/assets/img/breadcrumb-shape/frame.png",
        bee: "/assets/kidsa/assets/img/breadcrumb-shape/bee.png",
        main: "/assets/kidsa/assets/img/program/details-1.jpg",
        mision: "/assets/kidsa/assets/img/about/02.jpg",
        vision: "/assets/kidsa/assets/img/program/p-author.jpg"
    }
  };

  const radioData = {
    title: "Radio El Cristo"
  };

  const renderMainContent = () => {
    return (
      <>
        <KidsaHero {...heroData} />
        <div id="nosotros">
          <KidsaAboutUs {...aboutusData} />
        </div>
        <div id="modelo">
          <KidsaPrograms {...programsData} />
          <KidsaWorkProcess {...workProcessData} />
        </div>
        <div id="alumnos">
          <KidsaActivities {...activitiesData} />
        </div>
        <div id="profes">
          <KidsaTeam {...teamData} />
        </div>
        <KidsaVideo {...videoData} />
        <div id="comunicados">
          <KidsaTestimonial {...testimonialData} />
        </div>
        <div id="admision">
          <KidsaCta {...ctaData} />
        </div>
        <div id="noticias">
          <KidsaNews {...newsData} />
        </div>
        <div id="boletines">
          <KidsaNewsLetter {...newsletterData} />
        </div>
        <KidsaInstagram {...instagramData} />
        <KidsaBrands {...brandsData} />
      </>
    );
  };

  const subPageData = {
    nosotrosData,
    radioData
  };

  return (
    <div className="bg-white">
      <KidsaPreloader letters="EL CRISTO" />
      {/* <KidsaOffcanvas {...offcanvasData} />*/}
      <KidsaHeader {...headerData} />
      
      {/* Outlet componentes anidados en una subruta */}
      {isHomePage ? renderMainContent() : <Outlet context={subPageData} />}
      
      <div id="contacto">
        <KidsaFooter {...footerData} />
      </div>
    </div>
  );
};

export default ElCristo;